/**
 * AIF-3: Proof of Concept - AI Field Detection
 *
 * Simple POC to demonstrate:
 * 1. Upload PDF
 * 2. Extract text with coordinates (PDF.js)
 * 3. Send to backend AI API
 * 4. Display field suggestions
 * 5. Visualize suggestions on PDF
 *
 * Linear Issue: AIF-3
 */

import React, { useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, Box, Typography, Paper, CircularProgress, Card, CardContent, Chip } from '@mui/material';
import { extractTextWithCoordinates, groupTextIntoLines, detectFieldLabels } from '../services/pdfTextExtraction';
import { analyzePDFWithAI } from '../services/aiFieldDetection';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function AIFieldDetectionPOC() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // Extraction results
  const [textItems, setTextItems] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  const containerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(600);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setTextItems(null);
      setAiSuggestions([]);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleAnalyzeWithAI = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Extract text with coordinates (Frontend - AIF-1)
      setExtracting(true);
      console.log('🔍 Step 1: Extracting text from PDF...');

      const extractedTextItems = await extractTextWithCoordinates(selectedFile);
      const lines = groupTextIntoLines(extractedTextItems);
      const labels = detectFieldLabels(lines);

      setTextItems({ items: extractedTextItems, lines, labels });
      setExtracting(false);

      console.log('✅ Extraction complete:', {
        textItems: extractedTextItems.length,
        lines: lines.length,
        labels: labels.length
      });

      // DEBUG: Log text items near y=111 where AI detected field
      console.log('\n🔍 DEBUG: Text items near y=111:');
      extractedTextItems.filter(item => Math.abs(item.y - 111) < 20).forEach(item => {
        console.log(`  "${item.text}" @ x=${item.x.toFixed(2)} (${(item.x/612*100).toFixed(1)}%), y=${item.y.toFixed(2)}, width=${item.width.toFixed(2)}, dir=${item.dir}`);
      });

      // DEBUG: Check for underscores
      const underscores = extractedTextItems.filter(item => item.text.includes('_'));
      console.log(`\n🔍 DEBUG: Found ${underscores.length} text items with underscores`);
      if (underscores.length > 0) {
        underscores.forEach(item => console.log(`  "${item.text}" @ x=${item.x}`));
      } else {
        console.log('  ⚠️ No underscores found - PDF likely uses visual lines (graphics), not text');
      }

      // DEBUG: Log all lines
      console.log('\n📝 DEBUG: All text lines:');
      lines.forEach((line, i) => {
        console.log(`  ${i+1}. "${line.text}" @ x=${line.x.toFixed(2)}, y=${line.y.toFixed(2)}, width=${line.width.toFixed(2)}, dir=${line.dir}`);
      });

      // Check if PDF has no text (scanned PDF)
      if (extractedTextItems.length === 0) {
        setError('⚠️ This PDF has no text content (0 text items extracted). This is likely a scanned/image-based PDF. Please upload a text-based PDF created digitally (from Word, Google Docs, etc.) for AI field detection to work.');
        setExtracting(false);
        setLoading(false);
        return;
      }

      // Step 2: Send to AI for field type detection (Backend - AIF-2)
      setAnalyzing(true);
      console.log('🤖 Step 2: Sending to AI for field detection...');

      const aiResult = await analyzePDFWithAI({
        textItems: extractedTextItems,
        lines: lines,
        labels: labels
      });

      console.log('✅ AI analysis complete:', aiResult);
      setAiSuggestions(aiResult.suggestions || []);
      setAnalyzing(false);

    } catch (err) {
      console.error('❌ Analysis error:', err);
      console.error('❌ Error response:', err.response?.data);

      if (err.response?.status === 404) {
        setError('Backend API not ready yet. Please ensure backend is running and endpoint exists: POST /api/ai/detect-fields');
      } else if (err.response?.status === 400) {
        const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Bad request';
        setError(`Backend validation error: ${errorMsg}. This might be a scanned PDF with no text. Try a text-based PDF instead.`);
      } else {
        setError(`Analysis failed: ${err.message}`);
      }

      setExtracting(false);
      setAnalyzing(false);
    } finally {
      setLoading(false);
    }
  };

  const getSuggestionColor = (confidence) => {
    if (confidence >= 0.9) return '#4caf50'; // Green
    if (confidence >= 0.7) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.9) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };

  return (
    <Box sx={{ padding: 4, maxWidth: 1400, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        🤖 AI Field Detection - Proof of Concept (AIF-3)
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Upload a Hebrew/Arabic/English form → AI detects fields → Review suggestions
      </Typography>

      {/* Step 1: File Upload */}
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Upload PDF Form
        </Typography>

        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="pdf-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="pdf-upload">
          <Button variant="contained" component="span" sx={{ marginRight: 2 }}>
            Choose PDF File
          </Button>
        </label>

        {selectedFile && (
          <Typography variant="body2" component="span">
            ✅ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </Typography>
        )}
      </Paper>

      {/* Step 2: Analyze Button */}
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Analyze with AI
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={handleAnalyzeWithAI}
          disabled={!selectedFile || loading}
          sx={{ marginRight: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : '🤖 Analyze with AI'}
        </Button>

        {extracting && (
          <Typography variant="body2" component="span" color="primary">
            🔍 Extracting text from PDF...
          </Typography>
        )}

        {analyzing && (
          <Typography variant="body2" component="span" color="primary">
            🤖 AI analyzing field types...
          </Typography>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Paper sx={{ padding: 3, marginBottom: 3, bgcolor: '#ffebee' }}>
          <Typography variant="h6" color="error" gutterBottom>
            ⚠️ Error
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Results: Split View */}
      {aiSuggestions.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>

          {/* Left: PDF Preview with Overlay */}
          <Paper sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>
              📄 PDF Preview with Suggestions
            </Typography>

            <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
              <Document file={selectedFile} onLoadSuccess={handleDocumentLoadSuccess}>
                <Page
                  pageNumber={currentPage}
                  width={pdfWidth}
                  renderTextLayer={false}
                />
              </Document>

              {/* Overlay suggestions on PDF */}
              {aiSuggestions
                .filter(s => s.page === currentPage)
                .map((suggestion, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'absolute',
                      left: `${(suggestion.x / 595) * 100}%`, // PDF default width 595
                      top: `${(suggestion.y / 842) * 100}%`,  // PDF default height 842
                      border: `2px solid ${getSuggestionColor(suggestion.confidence)}`,
                      backgroundColor: `${getSuggestionColor(suggestion.confidence)}33`,
                      padding: '2px 6px',
                      borderRadius: 1,
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: getSuggestionColor(suggestion.confidence),
                      pointerEvents: 'none',
                      zIndex: 10
                    }}
                  >
                    {suggestion.fieldType}
                  </Box>
                ))}
            </div>

            {numPages > 1 && (
              <Box sx={{ marginTop: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Typography variant="body2" sx={{ padding: 1 }}>
                  Page {currentPage} of {numPages}
                </Typography>
                <Button
                  disabled={currentPage === numPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Paper>

          {/* Right: AI Suggestions List */}
          <Paper sx={{ padding: 3, maxHeight: 800, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              🤖 AI Detected Fields ({aiSuggestions.length})
            </Typography>

            {aiSuggestions.map((suggestion, index) => (
              <Card key={index} sx={{ marginBottom: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                    <Typography variant="h6" component="div">
                      {suggestion.fieldType}
                    </Typography>
                    <Chip
                      label={`${(suggestion.confidence * 100).toFixed(0)}% ${getConfidenceLabel(suggestion.confidence)}`}
                      size="small"
                      sx={{
                        bgcolor: getSuggestionColor(suggestion.confidence),
                        color: 'white'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ direction: suggestion.dir === 'rtl' ? 'rtl' : 'ltr', marginBottom: 1 }}
                  >
                    Label: "{suggestion.label}"
                  </Typography>

                  <Typography variant="caption" display="block" color="text.secondary">
                    📍 Position: ({Math.round(suggestion.x)}, {Math.round(suggestion.y)})
                    • Page {suggestion.page}
                    • {suggestion.dir.toUpperCase()}
                  </Typography>

                  {suggestion.reasoning && (
                    <Typography variant="caption" display="block" color="primary" sx={{ marginTop: 1 }}>
                      💡 {suggestion.reasoning}
                    </Typography>
                  )}

                  <Box sx={{ marginTop: 2, display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success">
                      ✓ Accept
                    </Button>
                    <Button size="small" variant="outlined">
                      ✎ Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error">
                      ✗ Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Box>
      )}

      {/* Extraction Stats (Debug Info) */}
      {textItems && (
        <Paper sx={{ padding: 3, marginTop: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom>
            📊 Extraction Statistics
          </Typography>
          <Typography variant="body2">
            • Text Items: {textItems.items.length}
            <br />
            • Text Lines: {textItems.lines.length}
            <br />
            • Detected Labels: {textItems.labels.length}
            <br />
            • AI Suggestions: {aiSuggestions.length}
          </Typography>
        </Paper>
      )}

      {/* Instructions */}
      <Paper sx={{ padding: 3, marginTop: 3, bgcolor: '#e3f2fd' }}>
        <Typography variant="h6" gutterBottom>
          📝 POC Flow
        </Typography>
        <Typography variant="body2" component="div">
          <ol>
            <li><strong>Upload:</strong> Select a Hebrew/Arabic/English form PDF</li>
            <li><strong>Extract:</strong> PDF.js extracts text with coordinates (AIF-1)</li>
            <li><strong>Analyze:</strong> Backend AI identifies field types (AIF-2)</li>
            <li><strong>Display:</strong> Shows suggestions with confidence scores</li>
            <li><strong>Review:</strong> User accepts/edits/rejects suggestions</li>
          </ol>
        </Typography>

        <Typography variant="body2" color="primary" sx={{ marginTop: 2 }}>
          ⚠️ <strong>Backend Required:</strong> Ensure backend is running with endpoint:
          <br />
          <code>POST {window.AppConfig.serverDomain}/api/ai/detect-fields</code>
        </Typography>
      </Paper>
    </Box>
  );
}
